import { m } from 'framer-motion';
// @mui
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
// routes
import { useRouter } from 'src/routes/hooks';
// auth
import { useAuthContext } from 'src/auth/hooks';
// components
import { varHover } from 'src/components/animate';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import { useSnackbar } from 'src/components/snackbar';
// path
import { useCallback, useMemo } from 'react';
import { useBoolean } from 'src/hooks/use-boolean';
import { paths } from 'src/routes/paths';
import DoiMatKhau from 'src/sections/doi-mat-khau/doi-mat-khau';

// ----------------------------------------------------------------------

const OPTIONS = [
  {
    label: 'Đổi mật khẩu',
    linkTo: null,
  },
];

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const router = useRouter();

  const { logout, user } = useAuthContext();

  const { enqueueSnackbar } = useSnackbar();

  const popover = usePopover();

  const showFormChangePassword = useBoolean();

  const src = useMemo(() => `${process.env.REACT_APP_IMAGE_HOST_API}${user?.photoURL}`, [user]);

  const handleLogout = async () => {
    try {
      await logout();
      // Xóa token
      localStorage.removeItem('accessToken');
      router.replace(paths.auth.jwt.login);
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Không thể đăng xuất !', { variant: 'error' });
    }
  };

  const handleClickItem = (path: string | null) => {
    popover.onClose();
    if (path) {
      router.push(path);
    } else {
      showFormChangePassword.onTrue();
    }
  };

  const handleCloseForm = useCallback(() => {
    showFormChangePassword.onFalse();
  }, []);

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        onClick={popover.onOpen}
        sx={{
          width: 40,
          height: 40,
          background: (theme) => alpha(theme.palette.grey[500], 0.08),
          ...(popover.open && {
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
          }),
        }}
      >
        <Avatar
          src={src}
          alt={user?.fullName}
          sx={{
            width: 36,
            height: 36,
            border: (theme) => `solid 2px ${theme.palette.background.default}`,
          }}
        />
      </IconButton>

      <CustomPopover open={popover.open} onClose={popover.onClose} sx={{ width: 200, p: 0 }}>
        <Box sx={{ p: 2, pb: 1.5 }}>
          <Typography variant="subtitle2" noWrap>
            {user?.fullName}
          </Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {user?.emailAddress}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Stack sx={{ p: 1 }}>
          {OPTIONS.map((option) => (
            <MenuItem key={option.label} onClick={() => handleClickItem(option.linkTo)}>
              {option.label}
            </MenuItem>
          ))}
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuItem
          onClick={handleLogout}
          sx={{ m: 1, fontWeight: 'fontWeightBold', color: 'error.main' }}
        >
          Đăng xuất
        </MenuItem>
      </CustomPopover>

      <DoiMatKhau
        onClose={handleCloseForm}
        open={showFormChangePassword.value}
        onLogout={handleLogout}
      />
    </>
  );
}
